// -----------------------------------------------------------
// FloraIQ - Nuvem de Pontos e Modelo 3D (Página Principal)
// -----------------------------------------------------------

import { useState, useCallback } from "react";
import PageHeader from "@/components/shared/PageHeader";
import KpiCard from "@/components/shared/KpiCard";
import { pointCloudKpiStats } from "@/mocks/pointcloud";
import PointCloudOverview from "./components/PointCloudOverview";
import SurveySection from "./components/SurveySection";
import PointCloudViewerMock from "./components/PointCloudViewerMock";
import TreeMetrics3DTable from "./components/TreeMetrics3DTable";
import CloudClassificationPanel from "./components/CloudClassificationPanel";
import Interference3DTable from "./components/Interference3DTable";
import Temporal3DComparison from "./components/Temporal3DComparison";
import PointCloudQualityPanel from "./components/PointCloudQualityPanel";
import PointCloudReportsGrid from "./components/PointCloudReportsGrid";
import PointCloudAssetDrawer from "./components/PointCloudAssetDrawer";
import NewSurveyModal from "./components/NewSurveyModal";
import LinkCloudToTreesModal from "./components/LinkCloudToTreesModal";

type TabId = "overview" | "surveys" | "viewer" | "metrics" | "classification" | "interferences" | "temporal" | "quality" | "reports";

const tabs: { id: TabId; label: string; icon: string }[] = [
  { id: "overview", label: "Visão Geral", icon: "ri-eye-line" },
  { id: "surveys", label: "Levantamentos", icon: "ri-flight-takeoff-line" },
  { id: "viewer", label: "Visualizador 3D", icon: "ri-stack-line" },
  { id: "metrics", label: "Métricas por Árvore", icon: "ri-plant-line" },
  { id: "classification", label: "Classificação", icon: "ri-palette-line" },
  { id: "interferences", label: "Interferências 3D", icon: "ri-error-warning-line" },
  { id: "temporal", label: "Comparação Temporal", icon: "ri-contrast-2-line" },
  { id: "quality", label: "Qualidade", icon: "ri-check-double-line" },
  { id: "reports", label: "Relatórios", icon: "ri-file-chart-line" },
];

export default function PointCloudPage() {
  const [activeTab, setActiveTab] = useState<TabId>("overview");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [newSurveyOpen, setNewSurveyOpen] = useState(false);
  const [linkModalOpen, setLinkModalOpen] = useState(false);

  const handleGoToViewer = useCallback(() => setActiveTab("viewer"), []);
  const handleGoToQuality = useCallback(() => setActiveTab("quality"), []);
  const handleGoToReports = useCallback(() => setActiveTab("reports"), []);

  const renderTab = () => {
    switch (activeTab) {
      case "overview": return <PointCloudOverview />;
      case "surveys": return <SurveySection />;
      case "viewer": return <PointCloudViewerMock />;
      case "metrics": return <TreeMetrics3DTable />;
      case "classification": return <CloudClassificationPanel />;
      case "interferences": return <Interference3DTable />;
      case "temporal": return <Temporal3DComparison />;
      case "quality": return <PointCloudQualityPanel />;
      case "reports": return <PointCloudReportsGrid />;
      default: return <PointCloudOverview />;
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Nuvem de Pontos e Modelo 3D"
        subtitle="Gerencie levantamentos com drone, LiDAR, laser scanner, ortofotos e métricas 3D vinculadas ao CTA e à metodologia FloraIQ-LADM."
      >
        <button
          onClick={() => setNewSurveyOpen(true)}
          className="btn-primary text-xs px-3 py-2 cursor-pointer whitespace-nowrap"
        >
          <i className="ri-add-line mr-1"></i>
          <span className="hidden sm:inline">Novo Levantamento</span>
        </button>
        <button className="btn-outline text-xs px-3 py-2 cursor-pointer whitespace-nowrap">
          <i className="ri-upload-2-line mr-1"></i>
          <span className="hidden sm:inline">Importar LAS/LAZ</span>
        </button>
        <button className="btn-outline text-xs px-3 py-2 cursor-pointer whitespace-nowrap">
          <i className="ri-image-line mr-1"></i>
          <span className="hidden sm:inline">Importar Ortofoto</span>
        </button>
        <button
          onClick={handleGoToQuality}
          className="btn-outline text-xs px-3 py-2 cursor-pointer whitespace-nowrap"
        >
          <i className="ri-cpu-line mr-1"></i>
          <span className="hidden sm:inline">Processar Métricas</span>
        </button>
        <button
          onClick={handleGoToViewer}
          className="btn-outline text-xs px-3 py-2 cursor-pointer whitespace-nowrap"
        >
          <i className="ri-stack-line mr-1"></i>
          <span className="hidden sm:inline">Abrir Visualizador 3D</span>
        </button>
        <button
          onClick={handleGoToReports}
          className="btn-outline text-xs px-3 py-2 cursor-pointer whitespace-nowrap"
        >
          <i className="ri-file-chart-line mr-1"></i>
          <span className="hidden sm:inline">Gerar Relatório 3D</span>
        </button>
      </PageHeader>

      {/* KPI Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
        <KpiCard
          label="Levantamentos Cadastrados"
          value={pointCloudKpiStats.levantamentosCadastrados}
          icon="ri-flight-takeoff-line"
          variant="info"
        />
        <KpiCard
          label="Nuvens de Pontos"
          value={pointCloudKpiStats.nuvensDePontos}
          icon="ri-stack-line"
          variant="info"
        />
        <KpiCard
          label="Ortofotos"
          value={pointCloudKpiStats.ortofotos}
          icon="ri-image-line"
          variant="default"
        />
        <KpiCard
          label="Árvores Vinculadas ao 3D"
          value={pointCloudKpiStats.arvoresVinculadas3D.toLocaleString("pt-BR")}
          icon="ri-plant-line"
          variant="success"
        />
        <KpiCard
          label="Alturas Extraídas"
          value={pointCloudKpiStats.alturasExtraidas.toLocaleString("pt-BR")}
          icon="ri-ruler-line"
          variant="success"
        />
        <KpiCard
          label="Copas Estimadas"
          value={pointCloudKpiStats.copasEstimadas.toLocaleString("pt-BR")}
          icon="ri-bubble-chart-line"
          variant="default"
        />
        <KpiCard
          label="Interferências Detectadas"
          value={pointCloudKpiStats.interferenciasDetectadas}
          icon="ri-error-warning-line"
          variant="warning"
        />
        <KpiCard
          label="Qualidade Média dos Metadados"
          value={`${pointCloudKpiStats.qualidadeMediaMetadados}%`}
          icon="ri-check-double-line"
          variant={pointCloudKpiStats.qualidadeMediaMetadados >= 80 ? "success" : "warning"}
        />
      </div>

      {/* Aviso técnico */}
      <div className="bg-blue-50/60 border border-blue-200/60 rounded-card p-4 text-sm text-foreground-600">
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0 mt-0.5">
            <i className="ri-information-line text-blue-600"></i>
          </div>
          <p>
            As visualizações e métricas 3D nesta etapa são simuladas. A estrutura está preparada para integração futura com processamento real 
            de nuvens de pontos, arquivos LAS/LAZ, LiDAR, laser scanner e modelos 3D (Potree, Cesium, Three.js, PDAL).
          </p>
        </div>
      </div>

      {/* Tabs Navigation */}
      <div className="bg-white rounded-card border border-background-200/70 overflow-hidden">
        <div className="border-b border-background-200/50 overflow-x-auto scrollbar-thin">
          <div className="flex px-1 pt-1 gap-0.5 min-w-max">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1.5 px-3 py-2.5 text-xs font-medium whitespace-nowrap transition-all duration-200 cursor-pointer rounded-t-md border-b-2 -mb-[1px] ${
                  activeTab === tab.id
                    ? "text-blue-600 border-blue-500 bg-blue-50/50"
                    : "text-foreground-500 border-transparent hover:text-foreground-700 hover:bg-background-50/50"
                }`}
              >
                <i className={`${tab.icon} text-sm`}></i>
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        <div className="p-5 fade-in" key={activeTab}>
          {renderTab()}
        </div>
      </div>

      {/* Actions bar */}
      <div className="flex items-center gap-2">
        <button
          onClick={() => setDrawerOpen(true)}
          className="btn-outline text-xs px-3 py-2 cursor-pointer whitespace-nowrap"
        >
          <i className="ri-eye-line mr-1"></i>Detalhe do Ativo 3D
        </button>
        <button
          onClick={() => setLinkModalOpen(true)}
          className="btn-outline text-xs px-3 py-2 cursor-pointer whitespace-nowrap"
        >
          <i className="ri-link mr-1"></i>Vincular Nuvem a Árvores
        </button>
      </div>

      {/* Modals & Drawers */}
      <PointCloudAssetDrawer isOpen={drawerOpen} onClose={() => setDrawerOpen(false)} />
      <NewSurveyModal isOpen={newSurveyOpen} onClose={() => setNewSurveyOpen(false)} />
      <LinkCloudToTreesModal isOpen={linkModalOpen} onClose={() => setLinkModalOpen(false)} />
    </div>
  );
}